import type { ColumnMetadata, LookupDataItem } from '../grid';
import { getProjectScopedLookupFieldType } from './lookupUtils';

const normalize = (value?: unknown): string =>
  String(value ?? '')
    .replace(/[^a-z0-9]/gi, '')
    .toLowerCase();

const toBoolean = (value: unknown): boolean | undefined => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;

  const normalized = String(value).trim().toLowerCase();
  if (!normalized) return undefined;
  if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
  if (['false', '0', 'no', 'n'].includes(normalized)) return false;

  return undefined;
};

const hasDeletedValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  const normalized = String(value).trim().toLowerCase();
  return normalized !== '' && normalized !== 'null' && normalized !== 'undefined';
};

const getLookupFieldValue = (item: LookupDataItem, aliases: string[]): string | undefined => {
  const aliasSet = new Set(aliases.map((alias) => normalize(alias)));
  const field = item.Fields?.find((entry) => aliasSet.has(normalize(entry.Name)));
  return field?.StringValue;
};

export const isActiveEmployeeLookupItem = (
  item: LookupDataItem,
  metadata?: ColumnMetadata
): boolean => {
  if (metadata && getProjectScopedLookupFieldType(metadata) !== 'employee') {
    return true;
  }

  const deletedAt = getLookupFieldValue(item, [
    'CrmRelationDeletedAt',
    'DeletedAt',
    'crmRelationDeletedAt',
    'deletedAt',
  ]);

  if (hasDeletedValue(deletedAt)) {
    return false;
  }

  const workingValue = getLookupFieldValue(item, [
    'CrmRelationWorking',
    'crmRelationWorking',
    'IsActive',
    'isActive',
  ]);

  const isWorking = toBoolean(workingValue);
  // If lookup payload doesn't include activity fields, keep backward-compatible behavior.
  return isWorking ?? true;
};

export const isActiveEmployeeRow = (row: Record<string, unknown>): boolean => {
  const deletedAt =
    row.crmRelationDeletedAt ?? row.CrmRelationDeletedAt ?? row.deletedAt ?? row.DeletedAt;

  if (hasDeletedValue(deletedAt)) {
    return false;
  }

  const working = row.crmRelationWorking ?? row.CrmRelationWorking ?? row.isActive ?? row.IsActive;
  const isWorking = toBoolean(working);

  // If source payload has no activity signal, preserve prior behavior.
  return isWorking ?? true;
};
